import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, TrendingDown, DollarSign, Percent } from 'lucide-react';
import axios from 'axios';
import useReveal from '@/hooks/useReveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MortgageCalculator({ lang }) {
  const { t } = useLanguage();
  const [price, setPrice] = useState(2000000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(3.75);
  const [tenure, setTenure] = useState('25');

  const loanAmount = price * (1 - downPct / 100);
  const monthlyRate = rate / 100 / 12;
  const months = parseInt(tenure) * 12;
  const monthly = monthlyRate > 0
    ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1)
    : loanAmount / months;
  const totalCost = monthly * months;
  const totalInterest = totalCost - loanAmount;
  const ltv = ((1 - downPct / 100) * 100).toFixed(0);

  const fmt = (n) => `AED ${Math.round(n).toLocaleString()}`;

  return (
    <div data-testid="mortgage-calculator" className="gmre-glass-light rounded-3xl p-8 lg:p-12 gmre-reveal">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0B2F44] to-[#071E2C] flex items-center justify-center shadow-lg shadow-[#0B2F44]/20">
          <Calculator className="w-5 h-5 text-[#C9A84C]" strokeWidth={1.5} />
        </div>
        <div>
          <div className="gmre-overline mb-1" style={{ fontSize: 10 }}>{lang === 'ar' ? 'حاسبة فورية' : 'Instant Estimate'}</div>
          <h3 className="text-2xl lg:text-3xl font-semibold text-[#0B2F44] tracking-tight" style={{ fontFamily: lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)' }}>
            {t('rates', 'calcTitle')}
          </h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Inputs */}
        <div className="space-y-8">
          {/* Price */}
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm text-[#4A6572]">{t('rates', 'propertyPrice')}</label>
              <span className="text-sm font-semibold text-[#0B2F44]" style={{ fontFamily: 'var(--font-en-mono)' }}>{fmt(price)}</span>
            </div>
            <Slider
              data-testid="calc-price-slider"
              value={[price]}
              onValueChange={([v]) => setPrice(v)}
              min={500000}
              max={50000000}
              step={100000}
              className="[&_[role=slider]]:bg-[#A68966] [&_[role=slider]]:border-[#A68966]"
            />
          </div>

          {/* Down Payment */}
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm text-[#4A6572]">{t('rates', 'downPayment')}</label>
              <span className="text-sm font-semibold text-[#0B2F44]">{downPct}%</span>
            </div>
            <Slider
              data-testid="calc-down-slider"
              value={[downPct]}
              onValueChange={([v]) => setDownPct(v)}
              min={15}
              max={50}
              step={1}
              className="[&_[role=slider]]:bg-[#A68966] [&_[role=slider]]:border-[#A68966]"
            />
          </div>

          {/* Rate */}
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm text-[#4A6572]">{t('rates', 'rate')}</label>
              <span className="text-sm font-semibold text-[#0B2F44]">{rate}%</span>
            </div>
            <Slider
              data-testid="calc-rate-slider"
              value={[rate]}
              onValueChange={([v]) => setRate(v)}
              min={2}
              max={8}
              step={0.01}
              className="[&_[role=slider]]:bg-[#A68966] [&_[role=slider]]:border-[#A68966]"
            />
          </div>

          {/* Tenure */}
          <div>
            <label className="text-sm text-[#4A6572] block mb-2">{t('rates', 'tenure')}</label>
            <Select value={tenure} onValueChange={setTenure}>
              <SelectTrigger data-testid="calc-tenure-select" className="border-[#D0D8DC]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[5, 10, 15, 20, 25].map(y => (
                  <SelectItem key={y} value={String(y)}>{y} {lang === 'ar' ? 'سنة' : 'years'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          <ResultCard icon={DollarSign} label={t('rates', 'monthly')} value={fmt(monthly)} highlight />
          <ResultCard icon={TrendingDown} label={t('rates', 'totalInterest')} value={fmt(totalInterest)} />
          <ResultCard icon={Calculator} label={t('rates', 'totalCost')} value={fmt(totalCost)} />
          <ResultCard icon={Percent} label={t('rates', 'ltv')} value={`${ltv}%`} />
        </div>
      </div>
    </div>
  );
}

function ResultCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-xl p-5 border ${highlight ? 'bg-[#0B2F44] border-[#0B2F44]' : 'bg-[#F8FAFB] border-[#D0D8DC]/50'}`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${highlight ? 'text-[#C9A84C]' : 'text-[#A68966]'}`} />
        <div>
          <p className={`text-xs ${highlight ? 'text-white/60' : 'text-[#4A6572]'}`}>{label}</p>
          <p className={`text-lg font-bold ${highlight ? 'text-white' : 'text-[#0B2F44]'}`} style={{ fontFamily: 'var(--font-en-mono)' }}>{value}</p>
        </div>
      </div>
    </div>
  );
}

function RateTable({ data, lang }) {
  const { t } = useLanguage();
  return (
    <div className="gmre-glass-light rounded-3xl overflow-hidden gmre-reveal">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gradient-to-r from-[#0B2F44] to-[#071E2C] hover:bg-[#0B2F44]">
              <TableHead className="text-white font-semibold text-[11px] tracking-[0.18em] uppercase py-4">{t('rates', 'product')}</TableHead>
              <TableHead className="text-white font-semibold text-[11px] tracking-[0.18em] uppercase">{t('rates', 'rateType')}</TableHead>
              <TableHead className="text-white font-semibold text-[11px] tracking-[0.18em] uppercase">{t('rates', 'bestRate')}</TableHead>
              <TableHead className="text-white font-semibold text-[11px] tracking-[0.18em] uppercase">{t('rates', 'notes')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, i) => (
              <TableRow key={i} data-testid={`rate-row-${i}`} className="hover:bg-white/40 border-b border-[#D0D8DC]/30 gmre-ios-transition">
                <TableCell className="font-medium text-[#0B2F44] text-sm tracking-tight">{row.product}</TableCell>
                <TableCell className="text-[#4A6572] text-xs uppercase tracking-wider">{row.rate_type}</TableCell>
                <TableCell className="text-base">
                  <span className="font-semibold text-[#0B2F44]" style={{ fontFamily: 'var(--font-en-display)' }}>
                    {row.best_rate}
                  </span>
                </TableCell>
                <TableCell className="text-[#4A6572] text-sm italic">{row.notes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function RatesPage() {
  useReveal();
  const { t, lang } = useLanguage();
  const displayFont = lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const [rates, setRates] = useState({ resident: [], non_resident: [] });

  useEffect(() => {
    axios.get(`${API}/rates`).then(r => setRates(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="rates-page">
      <section className="bg-[#071E2C] pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(201,168,76,0.14),transparent_60%)] pointer-events-none" />
        <div className="absolute inset-x-6 lg:inset-x-12 top-24 h-px bg-gradient-to-r from-transparent via-[#A68966]/30 to-transparent" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 text-center relative">
          <div className="gmre-overline justify-center mb-5 gmre-reveal">{lang === 'ar' ? 'أسعار تنافسية' : 'Live Market Rates'}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-semibold text-white mb-5 tracking-tight gmre-reveal gmre-reveal-delay-1" style={{ fontFamily: displayFont, lineHeight: 1.1, textWrap: 'balance' }}>
            {t('rates', 'title')}
          </h1>
          <div className="gmre-ornament mb-6 gmre-reveal gmre-reveal-delay-2"><div className="line" /><div className="dot" /><div className="line" /></div>
          <p className="text-white/65 max-w-2xl mx-auto text-base lg:text-lg leading-relaxed gmre-reveal gmre-reveal-delay-3">
            {lang === 'ar' ? 'أسعار محدّثة من 15+ بنكاً شريكاً · بدون رسوم وساطة' : 'Live rates from 15+ partner banks · zero brokerage fees'}
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFB] py-16 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 space-y-12">
          <Tabs defaultValue="resident">
            <TabsList className="bg-transparent gap-2 mb-8 justify-center w-full flex flex-wrap">
              <TabsTrigger
                value="resident"
                data-testid="rates-tab-resident"
                className="gmre-glass-light gmre-ios-transition px-7 py-3 rounded-full text-[#4A6572] data-[state=active]:bg-[#0B2F44] data-[state=active]:text-[#C9A84C] text-sm font-medium tracking-wide"
              >
                {t('rates', 'resident')}
              </TabsTrigger>
              <TabsTrigger
                value="nonresident"
                data-testid="rates-tab-nonresident"
                className="gmre-glass-light gmre-ios-transition px-7 py-3 rounded-full text-[#4A6572] data-[state=active]:bg-[#0B2F44] data-[state=active]:text-[#C9A84C] text-sm font-medium tracking-wide"
              >
                {t('rates', 'nonResident')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="resident">
              <RateTable data={rates.resident} lang={lang} />
            </TabsContent>
            <TabsContent value="nonresident">
              <RateTable data={rates.non_resident} lang={lang} />
            </TabsContent>
          </Tabs>

          <MortgageCalculator lang={lang} />
        </div>
      </section>
    </div>
  );
}
